import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Box, Typography, Paper, FormControl, InputLabel, Select, MenuItem, Button,
  TextField, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemText, IconButton, Grid, Switch, FormControlLabel,
  ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import WarningIcon from "@mui/icons-material/Warning";
import { useAuth } from "../context/AuthContext";
import {
  fetchPlayersForUser,
  fetchFeeds,
  fetchSchedulesForPlayer,
  createScheduleEntry,
  deleteScheduleEntry,
  ScheduleEntry,
  activateSchedule,
  deactivateSchedule,
  getPlayerInfo,
  PlayerInfo,
  getPlayerAdvertisingStatus,
  fetchAllowedContent,
  updateScheduleEntry,
} from "../api/api";
import PlayerSelector from "../components/Players/PlayerSelector";
import { useTranslation } from "react-i18next";

type Feed = {
  id: string;
  external_name: string;
  content_ids?: string[];
  content_metadata?: { [fileName: string]: { display_duration?: number } };
};

type Content = {
  file_name: string;
  content_name: string;
  mediatype?: string;
  duration?: number; // Wiedergabedauer in Millisekunden (für Videos)
};

const dayNames: { [key: number]: string } = {
  1: "monday",
  2: "tuesday",
  3: "wednesday",
  4: "thursday",
  5: "friday",
  6: "saturday",
  7: "sunday",
};

const Scheduling: React.FC = () => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();

  const [selectedPlayerSN, setSelectedPlayerSN] = useState<string | null>(null);
  const [availableFeeds, setAvailableFeeds] = useState<Feed[]>([]);
  const [selectedFeedId, setSelectedFeedId] = useState<string | null>(null);
  const [selectedFeedName, setSelectedFeedName] = useState<string | null>(null);

  // Initialisiere dayOfWeek mit dem heutigen Wochentag beim ersten Eintritt
  const getCurrentDayOfWeekForDropdown = (): number => {
    const today = new Date().getDay(); // 0=Sonntag, 1=Montag, ..., 6=Samstag
    return today === 0 ? 7 : today; // Konvertiere zu 1=Montag, ..., 7=Sonntag
  };
  const [dayOfWeek, setDayOfWeek] = useState<number>(getCurrentDayOfWeekForDropdown());
  const [startTime, setStartTime] = useState<string>("08:00:00");
  const [selectedDate, setSelectedDate] = useState<string>("");
  
  // State für den Modus: "weekday" oder "specificDate"
  const [scheduleMode, setScheduleMode] = useState<"weekday" | "specificDate">("weekday");
  
  // State für den aktuell angezeigten Tag im Karussell (1=Montag, 7=Sonntag)
  const getCurrentDayOfWeek = (): number => {
    const today = new Date().getDay(); // 0=Sonntag, 1=Montag, ..., 6=Samstag
    return today === 0 ? 7 : today; // Konvertiere zu 1=Montag, ..., 7=Sonntag
  };
  const [displayedDay, setDisplayedDay] = useState<number>(() => getCurrentDayOfWeek());
  
  // State für das aktuell angezeigte spezielle Datum im Karussell
  const [displayedSpecificDate, setDisplayedSpecificDate] = useState<string | null>(null);

  const [schedules, setSchedules] = useState<ScheduleEntry[]>([]);
  const [snackbarMsg, setSnackbarMsg] = useState<string | null>(null);

  const [confirmDeleteScheduleDialogOpen, setConfirmDeleteScheduleDialogOpen] = useState(false);
  const [scheduleEntryToDelete, setScheduleEntryToDelete] = useState<ScheduleEntry | null>(null);

  // Schedule-Status States
  const [activatingSchedule, setActivatingSchedule] = useState(false);

  // Player-Informationen (inkl. content_schedule_status)
  const [playerInfo, setPlayerInfo] = useState<PlayerInfo | null>(null);
  const [loadingPlayerInfo, setLoadingPlayerInfo] = useState(false);

  // State für aktuelle Zeit, um calculateNextFeed regelmäßig zu aktualisieren
  const [currentTime, setCurrentTime] = useState(new Date());

  // State für Timeout-Verwaltung
  const [scheduleToggleTimeout, setScheduleToggleTimeout] = useState<NodeJS.Timeout | null>(null);
  const [pendingToggleState, setPendingToggleState] = useState<boolean | null>(null); // Speichert die intendierte Stellung
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  
  // Ref für den vorherigen Status, um Statusänderungen zu erkennen
  const previousStatusRef = useRef<string | null | undefined>(null);
  // Ausgangszustand beim Klick (für Anzeige-Rückstellung nach Timeout)
  const originalScheduleStateRef = useRef<boolean>(false);
  // Override für Anzeige: nach Timeout ohne Response Schalter in Ausgangszustand zeigen
  const [scheduleDisplayOverride, setScheduleDisplayOverride] = useState<boolean | null>(null);

  // State für Content-Daten (für Gesamtdauer-Berechnung)
  const [allContent, setAllContent] = useState<Content[]>([]);

  // Werbe-Status für ausgewählten Player (Werbung möglich / Werbung aktiv)
  const [advertisingStatus, setAdvertisingStatus] = useState<"draft" | "active" | null>(null);

  const loadFeeds = useCallback(async () => {
    if (selectedPlayerSN) {
      try {
        const fetchedFeeds = await fetchFeeds(selectedPlayerSN);
        // Stelle sicher, dass content_metadata als Objekt geparst wird, falls es ein String ist
        const parsedFeeds = fetchedFeeds.map((feed: any) => ({
          ...feed,
          content_metadata: typeof feed.content_metadata === 'string' 
            ? JSON.parse(feed.content_metadata) 
            : feed.content_metadata
        }));
        setAvailableFeeds(parsedFeeds);
        if (selectedFeedId && !fetchedFeeds.some((f: Feed) => f.id === selectedFeedId)) {
          setSelectedFeedId(null);
          setSelectedFeedName(null);
        } else if (selectedFeedId) {
          const currentFeed = fetchedFeeds.find((f: Feed) => f.id === selectedFeedId);
          if (currentFeed) setSelectedFeedName(currentFeed.external_name);
        }
      } catch (error) {
        console.error("Error fetching feeds:", error);
        setSnackbarMsg(t("scheduling.errorLoadingFeeds"));
      }
    } else {
      setAvailableFeeds([]);
      setSelectedFeedId(null);
      setSelectedFeedName(null);
    }
  }, [selectedPlayerSN, selectedFeedId, t]);

  const loadSchedules = useCallback(async () => {
    if (selectedPlayerSN) {
      try {
        const fetchedSchedules = await fetchSchedulesForPlayer(selectedPlayerSN);
        const sortedSchedules = fetchedSchedules.sort((a: ScheduleEntry, b: ScheduleEntry) => {
          if (a.day_of_week !== b.day_of_week) {
            return a.day_of_week - b.day_of_week;
          }
          return a.start_time.localeCompare(b.start_time);
        });
        setSchedules(sortedSchedules);
      } catch (error) {
        console.error("Error fetching schedules:", error);
        setSnackbarMsg(t("scheduling.errorLoadingSchedules"));
      }
    } else {
      setSchedules([]);
    }
  }, [selectedPlayerSN, t]);

  // Content-Daten laden (für Gesamtdauer-Berechnung)
  const loadContent = useCallback(async () => {
    if (selectedPlayerSN) {
      try {
        const resp = await fetchAllowedContent(selectedPlayerSN);
        const all: Content[] = Array.isArray(resp) ? resp : resp.allowed || [];
        setAllContent(all);
      } catch (error) {
        console.error("Error fetching content:", error);
        // Fehler wird stillschweigend ignoriert, da Content optional ist
      }
    } else {
      setAllContent([]);
    }
  }, [selectedPlayerSN]);

  useEffect(() => {
    loadFeeds();
  }, [loadFeeds]);

  useEffect(() => {
    loadSchedules();
  }, [loadSchedules]);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  // Lade Player-Informationen (inkl. content_schedule_status)
  const loadPlayerInfo = useCallback(async () => {
    if (selectedPlayerSN) {
      setLoadingPlayerInfo(true);
      try {
        const info = await getPlayerInfo(selectedPlayerSN);
        const currentStatus = info.content_schedule_status;
        
        // Prüfe, ob eine Antwort vom Player eingetroffen ist (Status hat sich von "ordered" zu "processed" geändert)
        const previousStatus = previousStatusRef.current;
        if (isWaitingForResponse && pendingToggleState !== null && previousStatus) {
          // Wenn Status von "ordered" zu "processed" gewechselt ist
          if ((previousStatus === 'activation_ordered' && currentStatus === 'activation_processed') ||
              (previousStatus === 'deactivation_ordered' && currentStatus === 'deactivation_processed')) {
            // Antwort erhalten - lösche Timeout und setze Wartestatus zurück
            setScheduleToggleTimeout(prev => {
              if (prev) {
                clearTimeout(prev);
              }
              return null;
            });
            setPendingToggleState(null);
            setIsWaitingForResponse(false);
            setActivatingSchedule(false);
            setScheduleDisplayOverride(null); // Override aufheben, echte Response angekommen
          }
        }
        
        // Aktualisiere Ref mit neuem Status
        previousStatusRef.current = currentStatus;
        setPlayerInfo(info);
      } catch (error) {
        console.error("Error loading player info:", error);
        // Nicht als Fehler anzeigen, da der Endpunkt möglicherweise noch nicht implementiert ist
        setPlayerInfo(null);
      } finally {
        setLoadingPlayerInfo(false);
      }
    } else {
      setPlayerInfo(null);
    }
  }, [selectedPlayerSN, isWaitingForResponse, pendingToggleState]);

  useEffect(() => {
    loadPlayerInfo();
    // Lade Player-Info alle 10 Sekunden neu, um Status-Updates zu erhalten
    // Wenn wir auf eine Antwort warten, lade häufiger (alle 2 Sekunden)
    const interval = setInterval(() => {
      if (selectedPlayerSN) {
        loadPlayerInfo();
      }
    }, isWaitingForResponse ? 2000 : 10000);
    return () => clearInterval(interval);
  }, [loadPlayerInfo, selectedPlayerSN, isWaitingForResponse]);

  // Cleanup: Lösche Timeout beim Unmount
  useEffect(() => {
    return () => {
      if (scheduleToggleTimeout) {
        clearTimeout(scheduleToggleTimeout);
      }
    };
  }, [scheduleToggleTimeout]);

  // Beim Player-Wechsel Anzeige-Override zurücksetzen
  useEffect(() => {
    setScheduleDisplayOverride(null);
  }, [selectedPlayerSN]);

  useEffect(() => {
    if (!selectedPlayerSN) {
      setAdvertisingStatus(null);
      return;
    }
    getPlayerAdvertisingStatus(selectedPlayerSN)
      .then((r) => setAdvertisingStatus(r?.status ?? null))
      .catch(() => setAdvertisingStatus(null));
  }, [selectedPlayerSN]);

  // Timer für aktuelle Zeit, um calculateNextFeed regelmäßig zu aktualisieren
  useEffect(() => {
    // Aktualisiere sofort beim Mount
    setCurrentTime(new Date());
    
    // Berechne die Zeit bis zur nächsten vollen Minute
    const now = new Date();
    const msUntilNextMinute = 60000 - (now.getSeconds() * 1000 + now.getMilliseconds());
    
    let intervalId: NodeJS.Timeout | null = null;
    
    // Starte Timer nach der nächsten vollen Minute
    const initialTimeout = setTimeout(() => {
      setCurrentTime(new Date());
      // Dann alle Minute aktualisieren
      intervalId = setInterval(() => {
        setCurrentTime(new Date());
      }, 60000); // Jede Minute
    }, msUntilNextMinute);
    
    return () => {
      clearTimeout(initialTimeout);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []);

  // Hilfsfunktion: Normalisiere Datum zu YYYY-MM-DD Format
  const normalizeDate = (dateString: string | null | undefined): string | null => {
    if (!dateString) return null;
    // Wenn bereits im Format YYYY-MM-DD, direkt zurückgeben
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    // Versuche das Datum zu parsen und zu normalisieren
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    } catch (e) {
      // Fallback: Versuche das Datum direkt zu extrahieren
      const match = dateString.match(/(\d{4}-\d{2}-\d{2})/);
      if (match) {
        return match[1];
      }
    }
    return null;
  };

  // Aktualisiere Karussell, wenn ein Wochentag im Dropdown ausgewählt wird
  useEffect(() => {
    if (dayOfWeek !== 0) {
      setDisplayedDay(dayOfWeek);
      setScheduleMode("weekday");
    } else {
      setScheduleMode("specificDate");
    }
  }, [dayOfWeek]);
  
  // Initialisierung beim ersten Eintritt: Setze auf heutigen Wochentag
  useEffect(() => {
    if (selectedPlayerSN && dayOfWeek === getCurrentDayOfWeekForDropdown() && scheduleMode === "weekday") {
      // Stelle sicher, dass displayedDay auch gesetzt ist
      setDisplayedDay(dayOfWeek);
    }
  }, [selectedPlayerSN, dayOfWeek, scheduleMode]);

  // Aktualisiere Karussell, wenn ein Datum für "Spezielles Datum" ausgewählt wird
  useEffect(() => {
    if (dayOfWeek === 0 && selectedDate) {
      const normalizedDate = normalizeDate(selectedDate);
      if (normalizedDate) {
        setDisplayedSpecificDate(normalizedDate);
        const date = new Date(normalizedDate);
        const dayOfWeekFromDate = date.getDay(); // 0=Sonntag, 1=Montag, ..., 6=Samstag
        const dayOfWeekForDisplay = dayOfWeekFromDate === 0 ? 7 : dayOfWeekFromDate; // Konvertiere zu 1=Montag, ..., 7=Sonntag
        setDisplayedDay(dayOfWeekForDisplay);
      }
    } else if (dayOfWeek !== 0) {
      setDisplayedSpecificDate(null);
    }
  }, [dayOfWeek, selectedDate]);

  const handlePlayerChange = (sn: string) => {
    setSelectedPlayerSN(sn);
    // Beim Wechsel des Players: Setze auf heutigen Wochentag
    const today = getCurrentDayOfWeekForDropdown();
    setDayOfWeek(today);
    setDisplayedDay(today);
    setScheduleMode("weekday");
    setSelectedDate("");
    setDisplayedSpecificDate(null);
  };

  const handleFeedChange = (event: any) => {
    const feedId = event.target.value as string;
    setSelectedFeedId(feedId);
    const feed = availableFeeds.find(f => f.id === feedId);
    setSelectedFeedName(feed ? feed.external_name : null);
  };

  const handleDayChange = (event: any) => {
    const value = Number(event.target.value);
    setDayOfWeek(value);
    // Wenn nicht "Spezielles Datum" (0) ausgewählt, Datum zurücksetzen
    if (value !== 0) {
      setSelectedDate("");
      setDisplayedSpecificDate(null);
      setScheduleMode("weekday");
    } else {
      setScheduleMode("specificDate");
      // Wenn "Spezielles Datum" ausgewählt wird und ein Datum vorhanden ist, springe zu diesem Datum
      if (selectedDate) {
        setDisplayedSpecificDate(selectedDate);
        const date = new Date(selectedDate);
        const dayOfWeekFromDate = date.getDay();
        const dayOfWeekForDisplay = dayOfWeekFromDate === 0 ? 7 : dayOfWeekFromDate;
        setDisplayedDay(dayOfWeekForDisplay);
      } else if (availableSpecificDates.length > 0) {
        // Wenn kein Datum eingegeben, aber spezielle Datumseinträge vorhanden, springe zum ersten
        const firstDate = availableSpecificDates[0];
        setDisplayedSpecificDate(firstDate);
        setSelectedDate(firstDate);
        const date = new Date(firstDate);
        const dayOfWeekFromDate = date.getDay();
        const dayOfWeekForDisplay = dayOfWeekFromDate === 0 ? 7 : dayOfWeekFromDate;
        setDisplayedDay(dayOfWeekForDisplay);
      }
    }
  };

  // Handler für den Wechsel zwischen Wochentags und Spezielles Datum
  const handleScheduleModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newMode: "weekday" | "specificDate" | null,
  ) => {
    if (newMode === null) return; // Verhindere, dass beide deaktiviert werden
    
    setScheduleMode(newMode);
    
    if (newMode === "specificDate") {
      // Wechsel zu "Spezielles Datum": Setze dayOfWeek auf 0
      setDayOfWeek(0);
      // Wenn bereits ein Datum vorhanden ist, springe zu diesem
      if (selectedDate) {
        setDisplayedSpecificDate(selectedDate);
        const date = new Date(selectedDate);
        const dayOfWeekFromDate = date.getDay();
        const dayOfWeekForDisplay = dayOfWeekFromDate === 0 ? 7 : dayOfWeekFromDate;
        setDisplayedDay(dayOfWeekForDisplay);
      } else if (availableSpecificDates.length > 0) {
        // Springe zum ersten verfügbaren Datum
        const firstDate = availableSpecificDates[0];
        setDisplayedSpecificDate(firstDate);
        setSelectedDate(firstDate);
        const date = new Date(firstDate);
        const dayOfWeekFromDate = date.getDay();
        const dayOfWeekForDisplay = dayOfWeekFromDate === 0 ? 7 : dayOfWeekFromDate;
        setDisplayedDay(dayOfWeekForDisplay);
      }
    } else {
      // Wechsel zu "Wochentags": Setze auf heutigen Wochentag
      const today = getCurrentDayOfWeekForDropdown();
      setDayOfWeek(today);
      setDisplayedDay(today);
      setSelectedDate("");
      setDisplayedSpecificDate(null);
    }
  };

  const handleStartTimeChange = (event: any) => {
    const value = event.target.value;
    // Akzeptiere HH:MM:SS oder HH:MM (wird automatisch zu HH:MM:00)
    if (/^([01]\d|2[0-3]):([0-5]\d)(:([0-5]\d))?$/.test(value) || value === "") {
      // Wenn nur HH:MM eingegeben wurde, füge :00 hinzu
      if (value.length === 5) {
        setStartTime(`${value}:00`);
      } else {
        setStartTime(value);
      }
    }
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const dateValue = event.target.value;
    setSelectedDate(dateValue);
    // Wenn ein Datum eingegeben wird, springe im Karussell zu diesem Datum
    if (dateValue) {
      const normalizedDate = normalizeDate(dateValue);
      if (normalizedDate) {
        setDisplayedSpecificDate(normalizedDate);
        const date = new Date(normalizedDate);
        const dayOfWeekFromDate = date.getDay();
        const dayOfWeekForDisplay = dayOfWeekFromDate === 0 ? 7 : dayOfWeekFromDate;
        setDisplayedDay(dayOfWeekForDisplay);
      }
    }
  };

  // Berechne den tatsächlichen Wochentag für die API
  const getActualDayOfWeek = (): number => {
    if (dayOfWeek === 0 && selectedDate) {
      // Wenn "Spezielles Datum" ausgewählt und Datum vorhanden, berechne Wochentag
      const date = new Date(selectedDate);
      const dayOfWeekFromDate = date.getDay(); // 0=Sonntag, 1=Montag, ..., 6=Samstag
      return dayOfWeekFromDate === 0 ? 7 : dayOfWeekFromDate; // Konvertiere zu 1=Montag, ..., 7=Sonntag
    }
    return dayOfWeek;
  };

  const handleAddSchedule = async () => {
    if (!selectedPlayerSN || !selectedFeedId || !selectedFeedName || !startTime) {
      setSnackbarMsg(t("scheduling.fillAllFields"));
      return;
    }

    // Wenn "Spezielles Datum" ausgewählt, muss ein Datum vorhanden sein
    if (dayOfWeek === 0 && !selectedDate) {
      setSnackbarMsg(t("scheduling.fillAllFields"));
      return;
    }

    // startTime sollte bereits im Format HH:MM:SS sein
    const startTimeFull = startTime.length === 8 ? startTime : (startTime.length === 5 ? `${startTime}:00` : startTime);
    const actualDayOfWeek = getActualDayOfWeek();
    
    // Wenn "Spezielles Datum" ausgewählt, sende specific_date mit
    const specificDate = dayOfWeek === 0 && selectedDate ? selectedDate : null;

    try {
      await createScheduleEntry(
        selectedPlayerSN,
        actualDayOfWeek,
        startTimeFull,
        selectedFeedId,
        selectedFeedName,
        specificDate
      );
      setSnackbarMsg(t("scheduling.scheduleAdded"));
      await loadSchedules();
      await loadPlayerInfo();
      // Wenn "Spezielles Datum" ausgewählt, springe im Karussell zu diesem Datum
      if (dayOfWeek === 0 && specificDate) {
        const normalizedDate = normalizeDate(specificDate);
        if (normalizedDate) {
          setDisplayedSpecificDate(normalizedDate);
          const date = new Date(normalizedDate);
          const dayOfWeekFromDate = date.getDay();
          const dayOfWeekForDisplay = dayOfWeekFromDate === 0 ? 7 : dayOfWeekFromDate;
          setDisplayedDay(dayOfWeekForDisplay);
        }
      }
    } catch (error: any) {
      // --- DEBUGGING START ---
      console.error("Error adding schedule entry:", error);
      console.log("Error status:", error.status);
      console.log("Error data:", error.data);
      console.log("Error data.error:", error.data ? error.data.error : 'N/A');
      // --- DEBUGGING END ---

      if (error.status === 409 && error.data && error.data.error === "duplicate_schedule_entry") {
        setSnackbarMsg(t("scheduling.duplicateScheduleError"));
      } else {
        setSnackbarMsg(t("scheduling.errorAddingSchedule"));
      }
    }
  };

  const handleDeleteScheduleClick = (entry: ScheduleEntry) => {
    setScheduleEntryToDelete(entry);
    setConfirmDeleteScheduleDialogOpen(true);
  };

  const handleConfirmDeleteSchedule = async () => {
    if (scheduleEntryToDelete && selectedPlayerSN) {
      try {
        await deleteScheduleEntry(selectedPlayerSN, scheduleEntryToDelete.id);
        setSnackbarMsg(t("scheduling.scheduleDeleted"));
        await loadSchedules();
        await loadPlayerInfo();
      } catch (error: any) {
        console.error("Error deleting schedule entry:", error);
        setSnackbarMsg(t("scheduling.errorDeletingSchedule"));
      }
    }
    setConfirmDeleteScheduleDialogOpen(false);
    setScheduleEntryToDelete(null);
  };

  const handleCancelDeleteSchedule = () => {
    setConfirmDeleteScheduleDialogOpen(false);
    setScheduleEntryToDelete(null);
  };

  // Bestimme den aktuellen Status und ob der Zeitplan aktiviert ist
  // Berücksichtigt auch pendingToggleState für die Anzeige während des Timeouts
  const isScheduleActive = useMemo(() => {
    // Nach Timeout ohne Response: Schalter in Ausgangszustand anzeigen
    if (scheduleDisplayOverride !== null) return scheduleDisplayOverride;
    // Wenn wir auf eine Antwort warten und ein pendingToggleState gesetzt ist, verwende diesen
    if (isWaitingForResponse && pendingToggleState !== null) {
      return pendingToggleState;
    }
    // Normale Status-Bestimmung: aktiv bis deactivation_processed (bei deactivation_ordered ist der Zeitplan beim Player oft noch aktiv)
    if (!playerInfo?.content_schedule_status) return false;
    const status = playerInfo.content_schedule_status;
    return status === 'activation_ordered' || status === 'activation_processed' || status === 'deactivation_ordered';
  }, [playerInfo, isWaitingForResponse, pendingToggleState, scheduleDisplayOverride]);

  // Bestimme den Button-Titel basierend auf dem Status (für Disabled-Logik / Aria)
  const scheduleToggleLabel = useMemo(() => {
    if (scheduleDisplayOverride !== null) {
      return scheduleDisplayOverride ? t("scheduling.deactivateSchedule") : t("scheduling.activateSchedule");
    }
    if (isWaitingForResponse && pendingToggleState !== null) {
      return pendingToggleState ? t("scheduling.deactivateSchedule") : t("scheduling.activateSchedule");
    }
    if (!playerInfo?.content_schedule_status) {
      return t("scheduling.activateSchedule");
    }
    const status = playerInfo.content_schedule_status;
    if (status === 'activation_ordered' || status === 'activation_processed' || status === 'deactivation_ordered') {
      return t("scheduling.deactivateSchedule");
    }
    return t("scheduling.activateSchedule");
  }, [playerInfo, t, isWaitingForResponse, pendingToggleState, scheduleDisplayOverride]);

  // Konkretisierte Status-Anzeige: Aktueller / Veralteter Zeitplan aktiv oder Zeitplan inaktiv
  const scheduleStatusLabel = useMemo(() => {
    if (scheduleDisplayOverride !== null) {
      return scheduleDisplayOverride ? t("scheduling.deactivateSchedule") : t("scheduling.scheduleStatusInactive");
    }
    if (isWaitingForResponse && pendingToggleState !== null) {
      return pendingToggleState ? t("scheduling.deactivateSchedule") : t("scheduling.scheduleStatusInactive");
    }
    const status = playerInfo?.content_schedule_status;
    if (!status || status === 'deactivation_processed') {
      return t("scheduling.scheduleStatusInactive");
    }
    if (status === 'activation_ordered' || status === 'deactivation_ordered') {
      return t("scheduling.deactivateSchedule");
    }
    if (status === 'activation_processed') {
      const applied = playerInfo?.content_schedule_hash_applied;
      const current = playerInfo?.content_schedule_hash_current;
      // Abgleich mit vom VPS berechnetem Hash des aktuellen Zeitplans (content_schedule_hash_current)
      if (applied != null && current != null && applied !== current) {
        return t("scheduling.scheduleStatusOutdatedActive");
      }
      return t("scheduling.scheduleStatusCurrentActive");
    }
    return t("scheduling.scheduleStatusInactive");
  }, [playerInfo, t, isWaitingForResponse, pendingToggleState, scheduleDisplayOverride]);

  // Handler für den Schalter
  const handleScheduleToggle = async () => {
    if (!selectedPlayerSN) {
      setSnackbarMsg(t("scheduling.selectPlayerPrompt"));
      return;
    }

    if (!isScheduleActive && schedules.length === 0) {
      setSnackbarMsg(t("scheduling.noEntriesForDay"));
      return;
    }

    // Speichere die aktuelle Stellung (vor dem Klick) als Ausgangsstellung
    const currentState = isScheduleActive;
    const intendedState = !currentState; // Die intendierte neue Stellung
    originalScheduleStateRef.current = currentState;
    setScheduleDisplayOverride(null); // Override nur nach Timeout setzen

    // Setze pendingToggleState auf die intendierte Stellung
    setPendingToggleState(intendedState);
    setIsWaitingForResponse(true);
    setActivatingSchedule(true);

    // Ein einziger Timeout: wird vor allen API-Aufrufen gestartet, verhindert blockierendes Verhalten
    // (auch wenn activate/deactivate oder loadPlayerInfo hängen)
    if (scheduleToggleTimeout) {
      clearTimeout(scheduleToggleTimeout);
      setScheduleToggleTimeout(null);
    }
    const SAFETY_TIMEOUT_MS = 15000;
    const doRevertToOriginalState = () => {
      const original = originalScheduleStateRef.current;
      setPendingToggleState(null);
      setIsWaitingForResponse(false);
      setActivatingSchedule(false);
      setScheduleDisplayOverride(original); // Schalter in Ausgangszustand anzeigen
      setSnackbarMsg(t("scheduling.scheduleToggleTimeout"));
    };

    const timeoutId = setTimeout(() => {
      setIsWaitingForResponse(currentWaiting => {
        if (currentWaiting) {
          loadPlayerInfo()
            .then(() => {
              setTimeout(() => {
                setPlayerInfo(currentInfo => {
                  if (currentInfo?.content_schedule_status === 'activation_ordered' ||
                      currentInfo?.content_schedule_status === 'deactivation_ordered') {
                    doRevertToOriginalState();
                  }
                  return currentInfo;
                });
              }, 200);
            })
            .catch(() => {
              // Auch bei Fehler von loadPlayerInfo: Ausgangszustand wiederherstellen
              doRevertToOriginalState();
            });
        }
        return currentWaiting;
      });
      setScheduleToggleTimeout(null);
    }, SAFETY_TIMEOUT_MS);
    setScheduleToggleTimeout(timeoutId);

    try {
      if (currentState) {
        await deactivateSchedule(selectedPlayerSN);
      } else {
        await activateSchedule(selectedPlayerSN);
      }
      await loadPlayerInfo();
    } catch (error: any) {
      console.error("Error toggling schedule:", error);
      setSnackbarMsg(error?.data?.message || error?.message || t("scheduling.errorTogglingSchedule"));
      setPendingToggleState(null);
      setIsWaitingForResponse(false);
      setActivatingSchedule(false);
      clearTimeout(timeoutId);
      setScheduleToggleTimeout(null);
    }
  };

  // Hilfsfunktion: Extrahiere Zeit aus TIME WITH TIME ZONE Format (HH:MM:SS+HH:MM oder HH:MM:SS)
  const extractTimeFromTimeWithTZ = (timeStr: string): { hours: number; minutes: number; seconds: number } => {
    // Entferne Zeitzone-Information (+HH:MM oder -HH:MM)
    const timeOnly = timeStr.split(/[+-]/)[0];
    const parts = timeOnly.split(':').map(Number);
    return {
      hours: parts[0] || 0,
      minutes: parts[1] || 0,
      seconds: parts[2] || 0
    };
  };

  // Hilfsfunktion: Konvertiert UTC-Zeit in lokale Zeitzone
  const convertUTCToLocalTime = (utcTimeStr: string, timeZone: string | null | undefined): string => {
    if (!timeZone) {
      // Fallback: Zeige UTC-Zeit an
      const timeOnly = utcTimeStr.split(/[+-]/)[0];
      if (timeOnly.length === 5) {
        return `${timeOnly}:00`;
      }
      return timeOnly.substring(0, 8);
    }

    try {
      // Parse die UTC-Zeit (Format: HH:MM:SS+00:00 oder HH:MM:SS-00:00)
      const timeOnly = utcTimeStr.split(/[+-]/)[0];
      const [hours, minutes, seconds = '00'] = timeOnly.split(':');
      const h = parseInt(hours, 10);
      const m = parseInt(minutes, 10);
      const s = parseInt(seconds, 10);

      if (isNaN(h) || isNaN(m) || isNaN(s)) {
        return timeOnly.substring(0, 8);
      }

      // Erstelle ein Date-Objekt für heute mit der UTC-Zeit
      const today = new Date();
      const year = today.getUTCFullYear();
      const month = today.getUTCMonth();
      const day = today.getUTCDate();
      
      // Erstelle ein Date-Objekt in UTC
      const utcDate = new Date(Date.UTC(year, month, day, h, m, s));
      
      // Formatiere die Zeit in der lokalen Zeitzone
      const localFormatter = new Intl.DateTimeFormat('en', {
        timeZone: timeZone,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
      
      const localParts = localFormatter.formatToParts(utcDate);
      const localH = parseInt(localParts.find(p => p.type === 'hour')?.value || '0', 10);
      const localM = parseInt(localParts.find(p => p.type === 'minute')?.value || '0', 10);
      const localS = parseInt(localParts.find(p => p.type === 'second')?.value || '0', 10);
      
      return `${String(localH).padStart(2, '0')}:${String(localM).padStart(2, '0')}:${String(localS).padStart(2, '0')}`;
    } catch (error) {
      console.error('Fehler bei der Zeitzonenumrechnung:', error);
      // Fallback: Zeige UTC-Zeit an
      const timeOnly = utcTimeStr.split(/[+-]/)[0];
      if (timeOnly.length === 5) {
        return `${timeOnly}:00`;
      }
      return timeOnly.substring(0, 8);
    }
  };

  // Hilfsfunktion: Formatiere Zeit für Anzeige (HH:MM:SS) - konvertiert von UTC zu lokaler Zeitzone
  const formatTimeForDisplay = (timeStr: string): string => {
    // Verwende die Player-Zeitzone, falls verfügbar
    const playerTimeZone = playerInfo?.time_zone;
    return convertUTCToLocalTime(timeStr, playerTimeZone);
  };

  // Hilfsfunktion: Formatiere Dauer in MM:SS Format (erwartet Millisekunden)
  const formatDurationFromMs = (durationInMs: number): string => {
    if (!durationInMs || durationInMs <= 0) return '0:00';
    const totalSeconds = Math.floor(durationInMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  };

  // Berechne Gesamtdauer einer Playlist
  // Gibt null zurück, wenn nur ein Bild vorhanden ist und keine Anzeigedauer gesetzt ist (unbegrenzt)
  const calculateFeedTotalDuration = useCallback((feed: Feed): number | null => {
    if (!feed.content_ids || feed.content_ids.length === 0) {
      return 0;
    }

    // Prüfe, ob nur ein Bild vorhanden ist und keine Anzeigedauer gesetzt ist
    const contentItems = feed.content_ids.map(id => allContent.find(c => c.file_name === id)).filter(Boolean) as Content[];
    if (contentItems.length === 1) {
      const item = contentItems[0];
      if (item.mediatype?.toLowerCase() === 'image') {
        const metadata = feed.content_metadata?.[item.file_name];
        const displayDuration = metadata?.display_duration;
        // Wenn keine Dauer gesetzt ist, ist es unbegrenzt
        if (!displayDuration || displayDuration === 0) {
          return null; // null bedeutet "unbegrenzt"
        }
      }
    }

    let totalMs = 0;
    feed.content_ids.forEach((contentId, index) => {
      const contentItem = allContent.find(c => c.file_name === contentId);
      if (contentItem) {
        if (contentItem.mediatype?.toLowerCase() === 'video') {
          // Für Videos: duration ist in Millisekunden
          const durationMs = contentItem.duration || 0;
          totalMs += durationMs;
        } else if (contentItem.mediatype?.toLowerCase() === 'image') {
          // Für Bilder: Verwende display_duration aus Metadaten (in Millisekunden)
          const metadataKey = `${contentId}-${index}`;
          const metadata = feed.content_metadata?.[metadataKey] || feed.content_metadata?.[contentId];
          const displayDuration = metadata?.display_duration;
          // Nur hinzufügen, wenn eine Dauer explizit gesetzt ist
          if (displayDuration && displayDuration > 0) {
            totalMs += displayDuration;
          }
        }
      }
    });
    return totalMs;
  }, [allContent]);

  // Konvertiere Zeit-String (UTC in DB) zu Millisekunden seit Mitternacht (in lokaler Zeitzone)
  const timeToMs = (timeStr: string): number => {
    // Hole die Player-Zeitzone
    const playerTimeZone = playerInfo?.time_zone || 'UTC';
    
    try {
      // Extrahiere UTC-Zeit aus start_time (Format: "HH:MM:SS+00:00")
      const timeOnly = timeStr.split(/[+-]/)[0];
      const [hours, minutes, seconds = '00'] = timeOnly.split(':');
      const utcH = parseInt(hours, 10);
      const utcM = parseInt(minutes, 10);
      const utcS = parseInt(seconds, 10);
      
      if (isNaN(utcH) || isNaN(utcM) || isNaN(utcS)) {
        // Fallback: Behandle als lokale Zeit
        return (utcH * 3600 + utcM * 60 + utcS) * 1000;
      }
      
      // Erstelle ein Date-Objekt für heute mit der UTC-Zeit
      const today = new Date();
      const year = today.getUTCFullYear();
      const month = today.getUTCMonth();
      const day = today.getUTCDate();
      
      const utcDate = new Date(Date.UTC(year, month, day, utcH, utcM, utcS));
      
      // Formatiere die Zeit in der Player-Zeitzone
      const localFormatter = new Intl.DateTimeFormat('en', {
        timeZone: playerTimeZone,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
      
      const localParts = localFormatter.formatToParts(utcDate);
      const localH = parseInt(localParts.find(p => p.type === 'hour')?.value || '0', 10);
      const localM = parseInt(localParts.find(p => p.type === 'minute')?.value || '0', 10);
      const localS = parseInt(localParts.find(p => p.type === 'second')?.value || '0', 10);
      
      // Konvertiere lokale Zeit in Millisekunden seit Mitternacht
      return (localH * 3600 + localM * 60 + localS) * 1000;
    } catch (error) {
      console.error('Fehler bei timeToMs:', error);
      // Fallback: Behandle als lokale Zeit
      const timeOnly = timeStr.split(/[+-]/)[0];
      const parts = timeOnly.split(':');
      const hours = parseInt(parts[0] || '0', 10);
      const minutes = parseInt(parts[1] || '0', 10);
      const seconds = parseInt(parts[2] || '0', 10);
      return (hours * 3600 + minutes * 60 + seconds) * 1000;
    }
  };

  // Konvertiere Millisekunden seit Mitternacht zu Zeit-String (HH:MM:SS)
  const msToTime = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  // Prüfe, ob ein Schedule-Eintrag mit dem nächsten überlappt
  const hasOverlap = useCallback((entry: ScheduleEntry, allEntries: ScheduleEntry[]): ScheduleEntry | null => {
    const feed = availableFeeds.find(f => f.id === entry.feed_id);
    if (!feed) return null;

    const totalDuration = calculateFeedTotalDuration(feed);
    // Wenn unbegrenzt, keine Überlappung möglich
    if (totalDuration === null) return null;

    const entryStartMs = timeToMs(entry.start_time);
    const entryEndMs = entryStartMs + totalDuration;

    // Filtere Einträge nach gleichem Tag/Datum und sortiere nach Startzeit
    const relevantEntries = allEntries
      .filter(e => {
        if (entry.specific_date && e.specific_date) {
          // Beide haben spezifisches Datum - muss identisch sein
          return e.specific_date === entry.specific_date;
        } else if (!entry.specific_date && !e.specific_date) {
          // Beide haben Wochentag - muss identisch sein
          return e.day_of_week === entry.day_of_week;
        }
        return false;
      })
      .sort((a, b) => timeToMs(a.start_time) - timeToMs(b.start_time));

    // Finde den nächsten Eintrag nach dem aktuellen
    const currentIndex = relevantEntries.findIndex(e => e.id === entry.id);
    if (currentIndex === -1 || currentIndex === relevantEntries.length - 1) {
      return null; // Kein nächster Eintrag
    }

    const nextEntry = relevantEntries[currentIndex + 1];
    const nextEntryStartMs = timeToMs(nextEntry.start_time);

    // Überlappung, wenn Endzeit des aktuellen Eintrags nach Startzeit des nächsten Eintrags ist
    // Ignoriere Millisekunden-Unterschiede: Runde auf Sekunden (1000ms) und prüfe mit Toleranz
    const entryEndSeconds = Math.floor(entryEndMs / 1000);
    const nextStartSeconds = Math.floor(nextEntryStartMs / 1000);
    
    // Nur als Überlappung zählen, wenn die Differenz mehr als 1 Sekunde beträgt
    if (entryEndSeconds > nextStartSeconds) {
      return nextEntry;
    }

    return null;
  }, [availableFeeds, calculateFeedTotalDuration, playerInfo?.time_zone]);

  // Berechne nächsten Feed (Hilfsfunktion)
  const calculateNextFeed = useMemo(() => {
    if (!schedules.length) return null;

    // Hole die Player-Zeitzone
    const playerTimeZone = playerInfo?.time_zone || 'UTC';

    // Validierung: Prüfe ob currentTime gültig ist, sonst verwende aktuelles Datum
    let currentDate: Date;
    if (currentTime && !isNaN(new Date(currentTime).getTime())) {
      currentDate = new Date(currentTime);
    } else {
      console.warn('Invalid currentTime, using current date');
      currentDate = new Date();
    }
    
    // Berechne die aktuelle Zeit in der Player-Zeitzone
    const currentDateFormatter = new Intl.DateTimeFormat('en', {
      timeZone: playerTimeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    const currentDateParts = currentDateFormatter.formatToParts(currentDate);
    const currentYear = parseInt(currentDateParts.find(p => p.type === 'year')?.value || '0', 10);
    const currentMonth = parseInt(currentDateParts.find(p => p.type === 'month')?.value || '0', 10) - 1; // Monat ist 0-basiert
    const currentDayOfMonth = parseInt(currentDateParts.find(p => p.type === 'day')?.value || '0', 10);
    const currentH = parseInt(currentDateParts.find(p => p.type === 'hour')?.value || '0', 10);
    const currentM = parseInt(currentDateParts.find(p => p.type === 'minute')?.value || '0', 10);
    const currentS = parseInt(currentDateParts.find(p => p.type === 'second')?.value || '0', 10);
    
    // Erstelle ein Date-Objekt für die aktuelle Zeit in der Player-Zeitzone
    const currentDateInPlayerTZ = new Date(currentYear, currentMonth, currentDayOfMonth, currentH, currentM, currentS);
    const currentDay = currentDateInPlayerTZ.getDay(); // 0=Sonntag, 1=Montag, ..., 6=Samstag
    const currentTimeSeconds = currentH * 3600 + currentM * 60 + currentS;
    
    // Aktuelles Datum in der Player-Zeitzone (YYYY-MM-DD)
    const currentDateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(currentDayOfMonth).padStart(2, '0')}`;

    // Konvertiere alle Zeitplaneinträge in ein vergleichbares Format
    const upcomingFeeds = schedules
      .map(entry => {
        if (!entry.start_time) return null;
        
        try {
          // Extrahiere UTC-Zeit aus start_time
          const { hours: utcHours, minutes: utcMinutes, seconds: utcSeconds } = extractTimeFromTimeWithTZ(entry.start_time);
          
          // Konvertiere UTC-Zeit in die lokale Zeitzone des Players
          const today = new Date();
          const year = today.getUTCFullYear();
          const month = today.getUTCMonth();
          const day = today.getUTCDate();
          
          // Erstelle ein Date-Objekt für heute mit der UTC-Zeit
          const utcDate = new Date(Date.UTC(year, month, day, utcHours, utcMinutes, utcSeconds));
          
          // Formatiere die Zeit in der Player-Zeitzone
          const localFormatter = new Intl.DateTimeFormat('en', {
            timeZone: playerTimeZone,
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
          });
          
          const localParts = localFormatter.formatToParts(utcDate);
          const localH = parseInt(localParts.find(p => p.type === 'hour')?.value || '0', 10);
          const localM = parseInt(localParts.find(p => p.type === 'minute')?.value || '0', 10);
          const localS = parseInt(localParts.find(p => p.type === 'second')?.value || '0', 10);
          
          // Zielzeit in der Player-Zeitzone (in Sekunden)
          const targetTime = localH * 3600 + localM * 60 + localS;

          let daysUntilTarget: number;
          let targetDate: Date | null = null;

          // Wenn es ein spezielles Datum ist
          if (entry.specific_date) {
            // Prüfe, ob das Datum bereits ein vollständiges ISO-Datum ist oder nur YYYY-MM-DD
            let dateString = entry.specific_date;
            // Wenn es nur YYYY-MM-DD ist (kein 'T' enthalten), füge 'T00:00:00' hinzu
            if (!dateString.includes('T')) {
              dateString = dateString + 'T00:00:00';
            }
            const specificDate = new Date(dateString);
            
            // Validierung: Prüfe ob specificDate gültig ist
            if (isNaN(specificDate.getTime())) {
              console.warn('Invalid specific_date:', entry.specific_date);
              return null; // Ungültiges Datum, überspringe
            }
            
            // Validierung: Prüfe ob toISOString() funktioniert
            let specificDateStr: string;
            try {
              specificDateStr = specificDate.toISOString().split('T')[0];
            } catch (error) {
              console.warn('Error converting specific_date to ISO string:', error, entry.specific_date);
              // Fallback: Manuell formatieren
              const year = specificDate.getFullYear();
              const month = String(specificDate.getMonth() + 1).padStart(2, '0');
              const day = String(specificDate.getDate()).padStart(2, '0');
              specificDateStr = `${year}-${month}-${day}`;
            }
            
            // Wenn das Datum in der Vergangenheit liegt, überspringe es
            if (specificDateStr < currentDateStr) {
              return null; // Überspringe vergangene spezielle Datumseinträge
            }
            
            // Berechne Tage bis zum speziellen Datum
            const diffTime = specificDate.getTime() - currentDate.getTime();
            daysUntilTarget = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            
            // Wenn es heute ist, prüfe ob Zeit schon vorbei
            if (daysUntilTarget === 0 && currentTimeSeconds >= targetTime) {
              return null; // Überspringe, da Zeit bereits vorbei
            }
            
            targetDate = specificDate;
          } else {
            // Wochentags-basierter Eintrag
            const targetDay = entry.day_of_week === 7 ? 0 : entry.day_of_week; // 1=Montag -> 1, 7=Sonntag -> 0
            
            // Berechne Tage bis zum Zieltag
            daysUntilTarget = targetDay - currentDay;
            if (daysUntilTarget < 0) {
              daysUntilTarget += 7;
            }

            // Wenn heute der richtige Tag ist, prüfe ob Zeit schon vorbei
            if (daysUntilTarget === 0 && currentTimeSeconds >= targetTime) {
              daysUntilTarget = 7; // Nächste Woche
            }
          }

          return {
            entry,
            daysUntilTarget,
            targetTime,
            targetDate,
          };
        } catch (error) {
          console.warn('Error processing schedule entry:', error, entry);
          return null;
        }
      })
      .filter((item): item is NonNullable<typeof item> => item !== null)
      .sort((a, b) => {
        if (a.daysUntilTarget !== b.daysUntilTarget) {
          return a.daysUntilTarget - b.daysUntilTarget;
        }
        return a.targetTime - b.targetTime;
      });

    if (upcomingFeeds.length === 0) return null;

    const nextFeed = upcomingFeeds[0];
    
    // Bestimme Anzeige-Text für Tag
    let dayDisplay: string;
    if (nextFeed.entry.specific_date && nextFeed.targetDate) {
      // Formatiere spezielles Datum
      const date = nextFeed.targetDate;
      if (isNaN(date.getTime())) {
        // Fallback zu Wochentag wenn Datum ungültig
        const dayName = dayNames[nextFeed.entry.day_of_week];
        dayDisplay = t(`days.${dayName}`);
      } else {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        dayDisplay = `${day}.${month}.${year}`;
      }
    } else {
      // Wochentag
      const dayName = dayNames[nextFeed.entry.day_of_week];
      dayDisplay = t(`days.${dayName}`);
    }
    
    return {
      feedName: nextFeed.entry.feed_external_name,
      day: dayDisplay,
      time: formatTimeForDisplay(nextFeed.entry.start_time),
    };
  }, [schedules, t, currentTime, playerInfo?.time_zone]);

  const schedulesByDay = useMemo(() => {
    const grouped: { [key: number]: ScheduleEntry[] } = {};
    for (let i = 1; i <= 7; i++) {
      grouped[i] = [];
    }
    schedules.forEach((entry: ScheduleEntry) => {
      // Nur Einträge ohne specific_date zu den Wochentagen hinzufügen
      if (!entry.specific_date && grouped[entry.day_of_week]) {
        grouped[entry.day_of_week].push(entry);
      }
    });
    Object.values(grouped).forEach(daySchedules => {
      daySchedules.sort((a: ScheduleEntry, b: ScheduleEntry) => a.start_time.localeCompare(b.start_time));
    });
    return grouped;
  }, [schedules]);

  // Gruppiere spezielle Datumseinträge nach Datum
  const schedulesBySpecificDate = useMemo(() => {
    const grouped: { [date: string]: ScheduleEntry[] } = {};
    schedules.forEach((entry: ScheduleEntry) => {
      // Nur Einträge mit specific_date
      if (entry.specific_date) {
        const normalizedDate = normalizeDate(entry.specific_date);
        if (normalizedDate) {
          if (!grouped[normalizedDate]) {
            grouped[normalizedDate] = [];
          }
          grouped[normalizedDate].push(entry);
        }
      }
    });
    // Sortiere Einträge nach Startzeit
    Object.values(grouped).forEach(dateSchedules => {
      dateSchedules.sort((a: ScheduleEntry, b: ScheduleEntry) => a.start_time.localeCompare(b.start_time));
    });
    return grouped;
  }, [schedules]);

  // Hole alle verfügbaren speziellen Datumseinträge für den aktuellen Player
  const availableSpecificDates = useMemo(() => {
    return Object.keys(schedulesBySpecificDate).sort();
  }, [schedulesBySpecificDate]);

  // Normalisiere displayedSpecificDate, um sicherzustellen, dass es im richtigen Format ist
  const normalizedDisplayedSpecificDate = useMemo(() => {
    if (!displayedSpecificDate) return null;
    return normalizeDate(displayedSpecificDate);
  }, [displayedSpecificDate]);

  // Navigation für Karussell
  const handlePreviousDay = () => {
    if (dayOfWeek === 0 && displayedSpecificDate) {
      // Navigation durch spezielle Datumseinträge
      const currentIndex = availableSpecificDates.indexOf(displayedSpecificDate);
      if (currentIndex > 0) {
        const previousDate = availableSpecificDates[currentIndex - 1];
        setDisplayedSpecificDate(previousDate);
        const date = new Date(previousDate);
        const dayOfWeekFromDate = date.getDay();
        const dayOfWeekForDisplay = dayOfWeekFromDate === 0 ? 7 : dayOfWeekFromDate;
        setDisplayedDay(dayOfWeekForDisplay);
      } else if (availableSpecificDates.length > 0) {
        // Zum letzten Datum springen (zirkulär)
        const lastDate = availableSpecificDates[availableSpecificDates.length - 1];
        setDisplayedSpecificDate(lastDate);
        const date = new Date(lastDate);
        const dayOfWeekFromDate = date.getDay();
        const dayOfWeekForDisplay = dayOfWeekFromDate === 0 ? 7 : dayOfWeekFromDate;
        setDisplayedDay(dayOfWeekForDisplay);
      }
    } else {
      // Navigation durch Wochentage
      setDisplayedDay((prev) => {
        const newDay = prev === 1 ? 7 : prev - 1;
        // Aktualisiere auch das Dropdown, wenn nicht "Spezielles Datum" ausgewählt ist
        if (dayOfWeek !== 0) {
          setDayOfWeek(newDay);
        }
        return newDay;
      });
    }
  };

  const handleNextDay = () => {
    if (dayOfWeek === 0 && displayedSpecificDate) {
      // Navigation durch spezielle Datumseinträge
      const currentIndex = availableSpecificDates.indexOf(displayedSpecificDate);
      if (currentIndex < availableSpecificDates.length - 1) {
        const nextDate = availableSpecificDates[currentIndex + 1];
        setDisplayedSpecificDate(nextDate);
        const date = new Date(nextDate);
        const dayOfWeekFromDate = date.getDay();
        const dayOfWeekForDisplay = dayOfWeekFromDate === 0 ? 7 : dayOfWeekFromDate;
        setDisplayedDay(dayOfWeekForDisplay);
      } else if (availableSpecificDates.length > 0) {
        // Zum ersten Datum springen (zirkulär)
        const firstDate = availableSpecificDates[0];
        setDisplayedSpecificDate(firstDate);
        const date = new Date(firstDate);
        const dayOfWeekFromDate = date.getDay();
        const dayOfWeekForDisplay = dayOfWeekFromDate === 0 ? 7 : dayOfWeekFromDate;
        setDisplayedDay(dayOfWeekForDisplay);
      }
    } else {
      // Navigation durch Wochentage
      setDisplayedDay((prev) => {
        const newDay = prev === 7 ? 1 : prev + 1;
        // Aktualisiere auch das Dropdown, wenn nicht "Spezielles Datum" ausgewählt ist
        if (dayOfWeek !== 0) {
          setDayOfWeek(newDay);
        }
        return newDay;
      });
    }
  };

  // Formatiere Datum für Überschrift
  const formattedDate = useMemo(() => {
    if (dayOfWeek === 0 && selectedDate && selectedDate.length > 0) {
      try {
        const date = new Date(selectedDate + 'T00:00:00');
        if (!isNaN(date.getTime())) {
          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          return `${day}.${month}.${year}`;
        }
      } catch (e) {
        // Fallback
      }
    }
    return null;
  }, [dayOfWeek, selectedDate]);

  return (
    <Box>
      {/* Player auswählen - ohne Box */}
      <Box sx={{ mb: 2 }}>
        <PlayerSelector
          userId={user?.id || null}
          value={selectedPlayerSN}
          onChange={handlePlayerChange}
          disabled={false}
        />
        {!selectedPlayerSN && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t("scheduling.selectPlayerPrompt")}
          </Typography>
        )}
      </Box>

      {/* Zeitplan Box mit beiden Bereichen */}
      {selectedPlayerSN && (
        <Paper sx={{ p: 1.5 }}>
          <Typography variant="subtitle1" sx={{ mb: 3 }}>{t("scheduling.scheduleTitle")}</Typography>
          
          {/* Layout für große Screens: 1/3 links, 2/3 rechts */}
          <Box
            sx={{
              display: { xs: "block", md: "flex" },
              gap: 2,
              flexDirection: { xs: "column", md: "row" },
              alignItems: "flex-start",
              mb: 1,
            }}
          >
            {/* Linkes Drittel: Playlist einplanen */}
            <Box
              sx={{
                width: { xs: "100%", md: "33.333%" },
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 2, minHeight: '32px', display: 'flex', alignItems: 'center' }}>{t("scheduling.addScheduleEntry")}</Typography>
              <Box
                sx={{
                  border: "1px solid rgba(0, 0, 0, 0.12)",
                  borderRadius: 1,
                  p: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
              <FormControl fullWidth>
                <InputLabel id="feed-select-label" sx={{ fontSize: '0.875rem' }}>{t("scheduling.selectFeed")}</InputLabel>
                <Select
                  labelId="feed-select-label"
                  value={selectedFeedId || ""}
                  label={t("scheduling.selectFeed")}
                  onChange={handleFeedChange}
                  sx={{ fontSize: '0.875rem', '& .MuiOutlinedInput-root': { height: '48px' } }}
                >
                  {availableFeeds.length === 0 ? (
                    <MenuItem disabled sx={{ fontSize: '0.875rem' }}>{t("scheduling.noFeedsAvailable")}</MenuItem>
                  ) : (
                    availableFeeds.map((feed) => (
                      <MenuItem key={feed.id} value={feed.id} sx={{ fontSize: '0.875rem' }}>
                        {feed.external_name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
              {scheduleMode === "weekday" && (
                <FormControl fullWidth>
                  <InputLabel id="day-select-label" sx={{ fontSize: '0.875rem' }}>{t("scheduling.selectDay")}</InputLabel>
                  <Select
                    labelId="day-select-label"
                    value={dayOfWeek}
                    label={t("scheduling.selectDay")}
                    onChange={handleDayChange}
                    sx={{ fontSize: '0.875rem', '& .MuiOutlinedInput-root': { height: '48px' } }}
                  >
                    {Object.entries(dayNames).map(([num, name]) => (
                      <MenuItem key={num} value={Number(num)} sx={{ fontSize: '0.875rem' }}>
                        {t(`days.${name}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
                {dayOfWeek === 0 && (
                  <TextField
                    label={t("scheduling.selectDate")}
                    type="date"
                    value={selectedDate}
                    onChange={handleDateChange}
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                      sx: { fontSize: '0.875rem' },
                    }}
                    inputProps={{
                      sx: { fontSize: '0.875rem' },
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { height: '48px' } }}
                  />
                )}
              <TextField
                label={t("scheduling.startTime")}
                type="time"
                value={startTime}
                onChange={handleStartTimeChange}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                  sx: { fontSize: '0.875rem' },
                }}
                inputProps={{
                  step: 1, // 1 second
                  sx: { fontSize: '0.875rem' },
                }}
                sx={{ '& .MuiOutlinedInput-root': { height: '48px' } }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddSchedule}
                fullWidth
                startIcon={<AddIcon />}
                disabled={!selectedFeedId || !startTime}
                sx={{ fontSize: '0.875rem' }}
              >
                {t("scheduling.addEntry")}
              </Button>
              </Box>
            </Box>

            {/* Rechte zwei Drittel: Karussell für Wochentag */}
            <Box
              sx={{
                width: { xs: "100%", md: "66.666%" },
                mt: { xs: 2, md: 0 },
              }}
            >
              {/* Toggle für Wochentags / Spezielles Datum */}
              <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2, minHeight: '32px', alignItems: 'center' }}>
                <ToggleButtonGroup
                  value={scheduleMode}
                  exclusive
                  onChange={handleScheduleModeChange}
                  aria-label="Zeitplan-Modus"
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      padding: '2px 8px',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      textTransform: 'none',
                      minHeight: 'auto',
                      lineHeight: 1.5,
                      border: '1px solid rgba(0, 0, 0, 0.23)',
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        color: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.12)',
                        },
                      },
                    },
                  }}
                >
                  <ToggleButton value="weekday" aria-label="Wochentags">
                    {t("scheduling.weekdayTitle")}
                  </ToggleButton>
                  <ToggleButton value="specificDate" aria-label="Spezielles Datum">
                    {t("scheduling.specialDate")}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              {/* Karussell für Wochentag */}
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  minHeight: 300,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <IconButton
                    onClick={handlePreviousDay}
                    aria-label="Vorheriger Tag"
                    color="primary"
                    size="small"
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography variant="subtitle2" sx={dayOfWeek === 0 && !selectedDate ? { fontStyle: "italic" } : {}}>
                    {dayOfWeek === 0 && !selectedDate
                      ? t("scheduling.selectDateFirst")
                      : dayOfWeek === 0 && displayedSpecificDate
                      ? (() => {
                          try {
                            const date = new Date(displayedSpecificDate + 'T00:00:00');
                            if (!isNaN(date.getTime())) {
                              const day = String(date.getDate()).padStart(2, '0');
                              const month = String(date.getMonth() + 1).padStart(2, '0');
                              const year = date.getFullYear();
                              return `${day}.${month}.${year}`;
                            }
                          } catch (e) {
                            // Fallback
                          }
                          return displayedSpecificDate;
                        })()
                      : t(`days.${dayNames[displayedDay]}`)}
                  </Typography>
                  <IconButton
                    onClick={handleNextDay}
                    aria-label="Nächster Tag"
                    color="primary"
                    size="small"
                  >
                    <ArrowForwardIcon />
                  </IconButton>
                </Box>
                    {(() => {
                      // Wenn "Spezielles Datum" ausgewählt, aber kein Datum eingegeben, zeige nichts
                      if (dayOfWeek === 0 && !selectedDate) {
                        return null;
                      }
                      
                      // Bestimme welche Einträge angezeigt werden sollen
                      const entriesToShow = dayOfWeek === 0 && normalizedDisplayedSpecificDate
                        ? schedulesBySpecificDate[normalizedDisplayedSpecificDate] || []
                        : schedulesByDay[displayedDay] || [];
                      
                      return entriesToShow.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", mt: 2 }}>
                          {t("scheduling.noEntriesForDay")}
                        </Typography>
                      ) : (
                        <List
                          dense
                          sx={{
                            maxHeight: 400,
                            overflowY: "auto",
                            overflowX: "hidden",
                            flexGrow: 1,
                            "&::-webkit-scrollbar": { width: "8px" },
                            "&::-webkit-scrollbar-track": { backgroundColor: "grey.200", borderRadius: 1 },
                            "&::-webkit-scrollbar-thumb": {
                              backgroundColor: "grey.400",
                              borderRadius: 4,
                              "&:hover": { backgroundColor: "grey.500" },
                            },
                            scrollbarWidth: "thin",
                            scrollbarColor: "rgba(0, 0, 0, 0.3) rgba(0, 0, 0, 0.08)",
                          }}
                        >
                          {entriesToShow.map((entry: ScheduleEntry, index: number) => {
                            const feed = availableFeeds.find(f => f.id === entry.feed_id);
                            const totalDuration = feed ? calculateFeedTotalDuration(feed) : null;
                            const durationDisplay = totalDuration === null 
                              ? t("scheduling.unlimited") 
                              : formatDurationFromMs(totalDuration);
                            const overlappingEntry = hasOverlap(entry, entriesToShow);
                            
                            return (
                              <ListItem
                                key={entry.id}
                                button
                                onClick={() => {
                                  // Setze die Werte basierend auf dem Eintrag
                                  if (entry.specific_date) {
                                    // Spezielles Datum
                                    setScheduleMode("specificDate");
                                    setDayOfWeek(0);
                                    const normalizedDate = normalizeDate(entry.specific_date);
                                    if (normalizedDate) {
                                      setSelectedDate(normalizedDate);
                                      setDisplayedSpecificDate(normalizedDate);
                                      const date = new Date(normalizedDate);
                                      const dayOfWeekFromDate = date.getDay();
                                      const dayOfWeekForDisplay = dayOfWeekFromDate === 0 ? 7 : dayOfWeekFromDate;
                                      setDisplayedDay(dayOfWeekForDisplay);
                                    }
                                  } else {
                                    // Wochentag
                                    setScheduleMode("weekday");
                                    setDayOfWeek(entry.day_of_week);
                                    setDisplayedDay(entry.day_of_week);
                                    setSelectedDate("");
                                    setDisplayedSpecificDate(null);
                                  }
                                  // Setze Startzeit
                                  const timeOnly = formatTimeForDisplay(entry.start_time);
                                  setStartTime(timeOnly);
                                  // Setze Feed
                                  setSelectedFeedId(entry.feed_id);
                                  setSelectedFeedName(entry.feed_external_name);
                                }}
                                secondaryAction={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {overlappingEntry && (
                                      <IconButton 
                                        edge="end" 
                                        aria-label="fix overlap" 
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          if (!selectedPlayerSN || !overlappingEntry) return;
                                          
                                          try {
                                            // Kaskadierende Verschiebung: Behebe alle Überlappungen
                                            const maxDayMs = 24 * 60 * 60 * 1000; // 24 Stunden in Millisekunden
                                            
                                            // Erstelle eine lokale Kopie der Einträge mit aktualisierten Startzeiten
                                            const localEntries = entriesToShow.map(e => ({ ...e }));
                                            
                                            // Hilfsfunktion: Hole relevante Einträge für einen Eintrag (aus lokaler Kopie)
                                            const getRelevantEntries = (entry: ScheduleEntry) => {
                                              return localEntries
                                                .filter(e => {
                                                  if (entry.specific_date && e.specific_date) {
                                                    return e.specific_date === entry.specific_date;
                                                  } else if (!entry.specific_date && !e.specific_date) {
                                                    return e.day_of_week === entry.day_of_week;
                                                  }
                                                  return false;
                                                })
                                                .sort((a, b) => timeToMs(a.start_time) - timeToMs(b.start_time));
                                            };
                                            
                                            // Hilfsfunktion: Finde Eintrag in lokaler Kopie
                                            const findLocalEntry = (entryId: number) => {
                                              return localEntries.find(e => e.id === entryId);
                                            };
                                            
                                            // Hilfsfunktion: Finde Eintrag, der eine bestimmte Zeit blockiert
                                            // Ignoriert Millisekunden-Unterschiede (rundet auf Sekunden)
                                            const findBlockingEntry = (targetTimeMs: number, excludeEntryId: number, relevantEntries: ScheduleEntry[]): ScheduleEntry | null => {
                                              const targetSeconds = Math.floor(targetTimeMs / 1000);
                                              
                                              for (const e of relevantEntries) {
                                                if (e.id === excludeEntryId) continue;
                                                
                                                const feed = availableFeeds.find(f => f.id === e.feed_id);
                                                if (!feed) continue;
                                                
                                                const duration = calculateFeedTotalDuration(feed);
                                                const entryStartMs = timeToMs(e.start_time);
                                                const entryStartSeconds = Math.floor(entryStartMs / 1000);
                                                
                                                if (duration === null) {
                                                  // Unbegrenzte Dauer: Prüfe, ob die Zielzeit innerhalb des Eintrags liegt
                                                  // Finde den nächsten Nachfolger, um die implizite Dauer zu bestimmen
                                                  const entryIndex = relevantEntries.findIndex(ent => ent.id === e.id);
                                                  if (entryIndex < relevantEntries.length - 1) {
                                                    const nextEntry = relevantEntries[entryIndex + 1];
                                                    const nextStartMs = timeToMs(nextEntry.start_time);
                                                    const nextStartSeconds = Math.floor(nextStartMs / 1000);
                                                    // Prüfe auf Sekundenebene: targetSeconds muss >= entryStartSeconds und < nextStartSeconds sein
                                                    if (targetSeconds >= entryStartSeconds && targetSeconds < nextStartSeconds) {
                                                      return e;
                                                    }
                                                  } else {
                                                    // Kein Nachfolger, Eintrag läuft bis Tagesende
                                                    if (targetSeconds >= entryStartSeconds) {
                                                      return e;
                                                    }
                                                  }
                                                } else {
                                                  // Normale Dauer
                                                  const entryEndMs = entryStartMs + duration;
                                                  const entryEndSeconds = Math.floor(entryEndMs / 1000);
                                                  // Prüfe auf Sekundenebene: targetSeconds muss >= entryStartSeconds und < entryEndSeconds sein
                                                  if (targetSeconds >= entryStartSeconds && targetSeconds < entryEndSeconds) {
                                                    return e;
                                                  }
                                                }
                                              }
                                              return null;
                                            };
                                            
                                            // Funktion: Verschiebe einen Eintrag und alle blockierenden Einträge
                                            const shiftEntry = async (currentEntry: ScheduleEntry, entryToFix: ScheduleEntry): Promise<void> => {
                                              const localCurrentEntry = findLocalEntry(currentEntry.id);
                                              const localEntryToFix = findLocalEntry(entryToFix.id);
                                              
                                              if (!localCurrentEntry || !localEntryToFix) return;
                                              
                                              const feedCurrent = availableFeeds.find(f => f.id === localCurrentEntry.feed_id);
                                              if (!feedCurrent) return;
                                              
                                              const totalDuration = calculateFeedTotalDuration(feedCurrent);
                                              
                                              let newStartTimeMs: number;
                                              
                                              if (totalDuration === null) {
                                                // Unbegrenzte Dauer: Berechne Dauer aus Abstand zum nächsten Nachfolger
                                                const relevantEntries = getRelevantEntries(localCurrentEntry);
                                                const currentIndex = relevantEntries.findIndex(e => e.id === localCurrentEntry.id);
                                                
                                                if (currentIndex < relevantEntries.length - 1) {
                                                  const nextAfterUnlimited = relevantEntries[currentIndex + 1];
                                                  const currentStartMs = timeToMs(localCurrentEntry.start_time);
                                                  const nextStartMs = timeToMs(nextAfterUnlimited.start_time);
                                                  const calculatedDuration = nextStartMs - currentStartMs;
                                                  newStartTimeMs = currentStartMs + calculatedDuration;
                                                } else {
                                                  // Kein Nachfolger, kann nicht verschieben
                                                  return;
                                                }
                                              } else {
                                                // Normale Dauer
                                                const currentStartMs = timeToMs(localCurrentEntry.start_time);
                                                newStartTimeMs = currentStartMs + totalDuration;
                                              }
                                              
                                              // Prüfe Tagesgrenze
                                              if (newStartTimeMs >= maxDayMs) {
                                                setSnackbarMsg(t("scheduling.dayLimitReached"));
                                                return;
                                              }
                                              
                                              // Sammle alle blockierenden Einträge (können mehrere sein)
                                              const relevantEntries = getRelevantEntries(localEntryToFix);
                                              const blockingEntries: ScheduleEntry[] = [];
                                              let blockingEntry = findBlockingEntry(newStartTimeMs, entryToFix.id, relevantEntries);
                                              
                                              while (blockingEntry) {
                                                blockingEntries.push(blockingEntry);
                                                // Prüfe, ob der blockierende Eintrag selbst blockiert wird
                                                const blockingFeed = availableFeeds.find(f => f.id === blockingEntry!.feed_id);
                                                if (!blockingFeed) break;
                                                
                                                const blockingDuration = calculateFeedTotalDuration(blockingFeed);
                                                const blockingStartMs = timeToMs(blockingEntry.start_time);
                                                
                                                let blockingEndMs: number;
                                                if (blockingDuration === null) {
                                                  const blockingIndex = relevantEntries.findIndex(e => e.id === blockingEntry!.id);
                                                  if (blockingIndex < relevantEntries.length - 1) {
                                                    const nextAfterBlocking = relevantEntries[blockingIndex + 1];
                                                    blockingEndMs = timeToMs(nextAfterBlocking.start_time);
                                                  } else {
                                                    blockingEndMs = maxDayMs;
                                                  }
                                                } else {
                                                  blockingEndMs = blockingStartMs + blockingDuration;
                                                }
                                                
                                                // Prüfe, ob die Endzeit des blockierenden Eintrags auch blockiert ist
                                                blockingEntry = findBlockingEntry(blockingEndMs, blockingEntry.id, relevantEntries);
                                              }
                                              
                                              // Verschiebe alle blockierenden Einträge in umgekehrter Reihenfolge (vom letzten zum ersten)
                                              for (let i = blockingEntries.length - 1; i >= 0; i--) {
                                                const blocking = blockingEntries[i];
                                                const blockingFeed = availableFeeds.find(f => f.id === blocking.feed_id);
                                                if (!blockingFeed) continue;
                                                
                                                const blockingDuration = calculateFeedTotalDuration(blockingFeed);
                                                const blockingStartMs = timeToMs(blocking.start_time);
                                                
                                                let blockingEndMs: number;
                                                if (blockingDuration === null) {
                                                  const blockingIndex = relevantEntries.findIndex(e => e.id === blocking.id);
                                                  if (blockingIndex < relevantEntries.length - 1) {
                                                    const nextAfterBlocking = relevantEntries[blockingIndex + 1];
                                                    blockingEndMs = timeToMs(nextAfterBlocking.start_time);
                                                  } else {
                                                    blockingEndMs = maxDayMs;
                                                  }
                                                } else {
                                                  blockingEndMs = blockingStartMs + blockingDuration;
                                                }
                                                
                                                const newBlockingStartMs = blockingEndMs;
                                                
                                                if (newBlockingStartMs >= maxDayMs) {
                                                  setSnackbarMsg(t("scheduling.dayLimitReached"));
                                                  return;
                                                }
                                                
                                                const newBlockingStartTime = msToTime(newBlockingStartMs);
                                                const updatedBlockingEntry = await updateScheduleEntry(selectedPlayerSN, blocking.id, {
                                                  start_time: newBlockingStartTime
                                                });
                                                
                                                // Aktualisiere lokale Kopie mit der UTC-Zeit aus der API-Antwort
                                                const localBlockingEntry = findLocalEntry(blocking.id);
                                                if (localBlockingEntry && updatedBlockingEntry) {
                                                  localBlockingEntry.start_time = updatedBlockingEntry.start_time;
                                                }
                                              }
                                              
                                              // Jetzt kann entryToFix verschoben werden
                                              const newStartTime = msToTime(newStartTimeMs);
                                              const updatedEntry = await updateScheduleEntry(selectedPlayerSN, entryToFix.id, {
                                                start_time: newStartTime
                                              });
                                              
                                              // Aktualisiere lokale Kopie mit der UTC-Zeit aus der API-Antwort
                                              if (updatedEntry) {
                                                localEntryToFix.start_time = updatedEntry.start_time;
                                              }
                                              
                                              // Prüfe, ob entryToFix jetzt mit seinem Nachfolger überlappt
                                              const relevantEntriesAfterFix = getRelevantEntries(localEntryToFix);
                                              const fixedIndex = relevantEntriesAfterFix.findIndex(e => e.id === localEntryToFix.id);
                                              
                                              if (fixedIndex < relevantEntriesAfterFix.length - 1) {
                                                const nextAfterFixed = relevantEntriesAfterFix[fixedIndex + 1];
                                                const feedAfterFixed = availableFeeds.find(f => f.id === localEntryToFix.feed_id);
                                                
                                                if (feedAfterFixed) {
                                                  const durationAfterFixed = calculateFeedTotalDuration(feedAfterFixed);
                                                  
                                                  let fixedEndMs: number;
                                                  if (durationAfterFixed === null) {
                                                    // Unbegrenzte Dauer: Berechne aus nächstem Nachfolger
                                                    if (fixedIndex + 1 < relevantEntriesAfterFix.length - 1) {
                                                      const nextAfterUnlimited = relevantEntriesAfterFix[fixedIndex + 2];
                                                      fixedEndMs = timeToMs(nextAfterUnlimited.start_time);
                                                    } else {
                                                      fixedEndMs = maxDayMs;
                                                    }
                                                  } else {
                                                    fixedEndMs = newStartTimeMs + durationAfterFixed;
                                                  }
                                                  
                                                  const nextAfterFixedStartMs = timeToMs(nextAfterFixed.start_time);
                                                  
                                                  // Prüfe auf Sekundenebene (ignoriere Millisekunden-Unterschiede)
                                                  const fixedEndSeconds = Math.floor(fixedEndMs / 1000);
                                                  const nextAfterFixedStartSeconds = Math.floor(nextAfterFixedStartMs / 1000);
                                                  
                                                  if (fixedEndSeconds > nextAfterFixedStartSeconds) {
                                                    // Überlappung erkannt, verschiebe nächsten Eintrag rekursiv
                                                    await shiftEntry(localEntryToFix, nextAfterFixed);
                                                  }
                                                }
                                              }
                                            };
                                            
                                            // Starte die kaskadierende Verschiebung
                                            await shiftEntry(entry, overlappingEntry);
                                            
                                            setSnackbarMsg(t("scheduling.overlapFixed"));
                                            await loadSchedules();
                                            await loadPlayerInfo();
                                          } catch (error) {
                                            console.error("Error fixing overlap:", error);
                                            setSnackbarMsg(t("scheduling.errorFixingOverlap"));
                                          }
                                        }} 
                                        size="small"
                                        color="warning"
                                        title={t("scheduling.fixOverlap")}
                                      >
                                        <WarningIcon fontSize="small" />
                                      </IconButton>
                                    )}
                                    <IconButton edge="end" aria-label="delete" onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteScheduleClick(entry);
                                    }} size="small">
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                }
                              >
                                <ListItemText
                                  primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <span>{formatTimeForDisplay(entry.start_time)} - {entry.feed_external_name}</span>
                                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        ({t("scheduling.duration")}: {durationDisplay})
                                      </Typography>
                                    </Box>
                                  }
                                  secondary={
                                    advertisingStatus
                                      ? advertisingStatus === "active"
                                        ? t("scheduling.advertisingActive")
                                        : t("scheduling.advertisingPossible")
                                      : undefined
                                  }
                                  sx={advertisingStatus ? { "& .MuiListItemText-secondary": { fontSize: "0.8rem" } } : undefined}
                                />
                              </ListItem>
                            );
                          })}
                        </List>
                      );
                    })()}
        </Paper>
            </Box>
          </Box>

          {/* Status-Anzeige: gleiches Spalten-Layout wie oben – „Nächste geplante Playlist“ linksbündig zur Schedule-Box, Schalter rechtsbündig */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", md: "row" },
              alignItems: "stretch",
            }}
          >
            {/* Spacer: gleiche Breite wie linkes Drittel („Playlist einplanen“), damit rechte Spalte mit Schedule-Box fluchtet */}
            <Box sx={{ width: { xs: 0, md: "33.333%" }, flexShrink: 0, display: { xs: "none", md: "block" } }} />
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              {calculateNextFeed && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.875rem",
                    color: isScheduleActive ? '#2e7d32' : '#616161',
                    fontWeight: 500,
                  }}
                >
                  {t("scheduling.nextFeed")}: {calculateNextFeed.feedName} ({calculateNextFeed.day}, {calculateNextFeed.time})
                </Typography>
              )}
              {/* Schalter für Aktivieren/Deaktivieren (rechtsbündig) */}
              <FormControlLabel
                control={
                <Switch
                  checked={isScheduleActive}
                  onChange={handleScheduleToggle}
                  disabled={
                    activatingSchedule ||
                    (schedules.length === 0 && !isScheduleActive) ||
                    isWaitingForResponse
                    // activation_ordered/deactivation_ordered nur indirekt: während isWaitingForResponse ist der Schalter ohnehin deaktiviert.
                    // Nach Timeout oder bei Seiten-Reload mit noch "ordered" in der DB soll erneut geklickt werden können.
                  }
                  color="primary"
                />
                }
                label={scheduleStatusLabel}
                sx={{
                  '& .MuiFormControlLabel-label': {
                    color: isScheduleActive ? '#2e7d32' : '#616161', // Dunkelgrün wenn aktiv, dunkelgrau wenn inaktiv
                    fontWeight: 500,
                    fontSize: "0.875rem", // Gleiche Schriftgröße wie "Nächste geplante Playlist"
                  }
                }}
              />
            </Box>
          </Box>
        </Paper>
      )}

      <Dialog
        open={confirmDeleteScheduleDialogOpen}
        onClose={handleCancelDeleteSchedule}
        aria-labelledby="confirm-delete-schedule-title"
        aria-describedby="confirm-delete-schedule-description"
      >
        <DialogTitle id="confirm-delete-schedule-title">{t("scheduling.confirmDeleteTitle")}</DialogTitle>
        <DialogContent>
          <Typography id="confirm-delete-schedule-description">
            {t("scheduling.confirmDeleteMessage", {
              feedName: scheduleEntryToDelete?.feed_external_name,
              day: t(`days.${dayNames[scheduleEntryToDelete?.day_of_week || 1]}`),
              time: scheduleEntryToDelete ? formatTimeForDisplay(scheduleEntryToDelete.start_time) : '',
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDeleteSchedule} color="primary">{t("common.cancel")}</Button>
          <Button onClick={handleConfirmDeleteSchedule} color="error" autoFocus>{t("common.delete")}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbarMsg}
        autoHideDuration={3000}
        onClose={() => setSnackbarMsg(null)}
        message={snackbarMsg}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      />
    </Box>
  );
};

export default Scheduling;
